import React, { useState, useEffect, useCallback } from 'react';
import { useMutation } from '@tanstack/react-query';
import { ReactComponent as KeycloakIcon } from '@assets/keycloak.svg';
import { useSearchParams } from 'react-router-dom';
import { Users, Download, Plus, UserX, Edit, Eye, Trash2 } from 'lucide-react';
import { useAuth, usePermissions } from '@contexts/AuthContext';
import { userAdminService, rolesService, divisionService } from '@services';
import type { Role } from '@app-types';
import { useDebounce } from '@hooks/useDebounce';
import { usePagination } from '@hooks/usePagination';
import { useColumnFilters } from '@hooks/useColumnFilters';
import { useTableSort } from '@hooks/useTableSort';
import { useUserPageData } from '@hooks/useUserPageData';
import PageLayout from '@components/layout/PageLayout';
import ListPageHeader from '@components/common/ListPageHeader';
import { FilterButtons, FilterOption } from '@components/common/FilterButtons';
import { RZTable, ColumnSelector } from '@components/common/table';
import RoleFilterDropdown from '@components/common/RoleFilterDropdown';
import { GenericForm } from '@components/common/forms';
import { GenericModal } from '@components/common/modals';
import ConfirmActionModal from '@components/admin/ConfirmActionModal';
import UserSyncModal from '@components/admin/UserSyncModal';
import { buildCreateUserFormFields, buildEditUserFormFields } from '@components/features/users';
import { isMsalProvider } from '@config/authConfig';
import Button from '@components/common/Button';
import Badge from '@components/common/Badge';
import { TableColumn, ROLES } from '@app-types';
import { AdminUser, UserFilters } from '@app-types/admin';
import { CONTACT_STATUS, NON_USER_ID } from '@app-types/user';
import { LoadingOverlay } from '@components/common/loading';
import { exportUsers } from '@utils/exportUtils';
import { formatPersonName } from '@utils/stringUtils';
import { hasRole } from '@utils/permissionUtils';
import { showToast } from '@utils/toast';
import { getApiErrorMessage } from '@utils/apiError';
import {
  buildCreatePayload,
  getCanLoginFieldState,
  getChangedFields,
  getContactStatusOverrides,
  getRoleChangeUserIdOverrides,
  getUserIdFieldState,
  initialContactStatus,
  isDefaultUserId,
  isEligibleForTemporaryPassword,
  resolveUserFormSubmitError,
  USER_ID_REQUIRED_FOR_ROLES,
  toContactStatusFormValue,
  toEditFormInitialData,
  toEditFormOriginal,
  toRoleIds,
  toRoleIdStrings,
  withoutKey
} from '@utils/userForm';
import type { FormField } from '@components/common/forms';
import type { ContactStatusFormValue, UserFormData, UserFormValue } from '@app-types/userForm';
import styles from './UserManagementPage.module.css';

const DUPLICATE_EMAIL_MESSAGE = 'A user with this email address already exists. Please use a different email.';

// Default Division for a new Contact-User (R7); matched by name, as the server does.
const UNIDENTIFIED_DIVISION_NAME = 'Unidentified';

// Filter options for status/type filtering
const statusFilters: FilterOption[] = [
  { key: 'all', label: 'All' },
  { key: 'active', label: 'Active' },
  { key: 'inactive', label: 'Inactive' },
  { key: 'contacts', label: 'Contacts' }
];

// v4: UserID column added (ADO-1162); a stored v3 set would keep it hidden.
const VISIBLE_COLUMNS_STORAGE_KEY = 'users-visible-columns-v4';

// Column configuration with filter keys for backend
const userColumns: TableColumn[] = [
  { key: 'email', label: 'Email', defaultVisible: true, sortable: true, filterKey: 'email' },
  { key: 'first_name', label: 'Name', defaultVisible: true, sortable: true, filterKey: 'name' },
  { key: 'contact_id', label: 'ContactID', defaultVisible: true, sortable: true, filterKey: 'contact_id' },
  { key: 'user_id', label: 'UserID', defaultVisible: true, sortable: true, filterKey: 'user_id' },
  { key: 'role_names', label: 'Roles', defaultVisible: true, sortable: true, filterKey: 'role_name' },
  { key: 'division_name', label: 'Division', defaultVisible: true, sortable: false, filterable: false },
  { key: 'auth_source', label: 'Auth', defaultVisible: true, centered: true, sortable: false, filterable: false },
  {
    key: 'account_source',
    label: 'Account Source',
    defaultVisible: true,
    centered: true,
    sortable: true,
    filterKey: 'account_source'
  },
  { key: 'is_active', label: 'Status', defaultVisible: true, sortable: true, filterKey: 'is_active' },
  { key: 'can_login', label: 'Login Access', defaultVisible: true, sortable: true, filterKey: 'can_login' },
  { key: 'actions', label: 'Actions', defaultVisible: true, centered: true, sortable: false, filterable: false }
];

const UserManagementPage: React.FC = () => {
  const { user: currentUser } = useAuth();
  // Program Lead sees Contact-Users read-only; changes are requested from an Admin (ADO-1143)
  const { canManageUsers: canManageContactUsers } = usePermissions();
  const [searchParams, setSearchParams] = useSearchParams();

  // State from URL parameters
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');
  const [selectedRoleIds, setSelectedRoleIds] = useState<number[]>([]);

  // Pagination with localStorage persistence
  const { currentPage, pageSize, setCurrentPage, setPageSize, resetToFirstPage } = usePagination({
    storageKey: 'users-page-size',
    defaultPageSize: 25
  });

  // Debounced search
  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  // Column filters with URL persistence
  const {
    filterValues: columnFilterValues,
    debouncedFilterValues: debouncedColumnFilters,
    setFilter: setColumnFilter,
    clearAllFilters: clearColumnFilters,
    hasActiveFilters: hasActiveColumnFilters,
    getApiParam: getColumnFiltersParam
  } = useColumnFilters({ columns: userColumns });

  // Other state
  const [roles, setRoles] = useState<Role[]>([]);
  const [divisions, setDivisions] = useState<{ id: number; name: string }[]>([]);

  // Sorting with hook
  const { sortConfig, handleSort, isSorted } = useTableSort({
    columnMapping: {
      email: 'email',
      first_name: 'first_name',
      contact_id: 'contact_id',
      user_id: 'user_id',
      role_names: 'role_names',
      account_source: 'account_source',
      is_active: 'is_active',
      can_login: 'can_login',
      created_at: 'created_at'
    },
    defaultSort: { field: 'created_at', direction: 'DESC' },
    onPageReset: () => setCurrentPage(1)
  });

  // Column visibility
  const [visibleColumns, setVisibleColumns] = useState<Set<string>>(() => {
    const stored = localStorage.getItem(VISIBLE_COLUMNS_STORAGE_KEY);
    if (stored) return new Set(JSON.parse(stored));
    const defaultColumns = userColumns.filter((col) => col.defaultVisible).map((col) => col.key);
    return new Set(defaultColumns);
  });

  // Modal States
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeactivateModalOpen, setIsDeactivateModalOpen] = useState(false);
  const [isSendPasswordModalOpen, setIsSendPasswordModalOpen] = useState(false);
  const [isSyncModalOpen, setIsSyncModalOpen] = useState(false);
  const [isSendingTemporaryPassword, setIsSendingTemporaryPassword] = useState(false);
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [createExternalErrors, setCreateExternalErrors] = useState<Record<string, string>>({});
  const [editExternalErrors, setEditExternalErrors] = useState<Record<string, string>>({});

  // Form state for conditional fields
  const [createFormRoleIds, setCreateFormRoleIds] = useState<string[]>([]);
  const [createFormContactStatus, setCreateFormContactStatus] = useState<ContactStatusFormValue>('');
  const [createFormOverrides, setCreateFormOverrides] = useState<Partial<UserFormData>>();
  const [editFormRoleIds, setEditFormRoleIds] = useState<string[]>([]);
  const [editFormContactStatus, setEditFormContactStatus] = useState<ContactStatusFormValue>(CONTACT_STATUS.ACTIVE);
  const [editFormOverrides, setEditFormOverrides] = useState<Partial<UserFormData>>();

  // Save column preferences
  useEffect(() => {
    localStorage.setItem(VISIBLE_COLUMNS_STORAGE_KEY, JSON.stringify(Array.from(visibleColumns)));
  }, [visibleColumns]);

  // Update URL when filters change.
  // Clone existing params so cf_* column filters (and page/limit) are preserved.
  // Pagination is owned by usePagination; do not rewrite page here.
  useEffect(() => {
    const params = new URLSearchParams(searchParams);

    if (debouncedSearchQuery) {
      params.set('search', debouncedSearchQuery);
    } else {
      params.delete('search');
    }

    if (statusFilter !== 'all') {
      params.set('status', statusFilter);
    } else {
      params.delete('status');
    }

    setSearchParams(params, { replace: true });
  }, [debouncedSearchQuery, statusFilter, searchParams, setSearchParams]);

  useEffect(() => {
    fetchRoles();
    fetchDivisions();
  }, []);

  const fetchRoles = async () => {
    try {
      const rolesData = await rolesService.getRoles();
      setRoles(rolesData);
    } catch (error) {
      console.error('Failed to fetch roles', error);
    }
  };

  const fetchDivisions = async () => {
    try {
      const divisionsData = await divisionService.getActive();
      setDivisions(divisionsData);
    } catch (error) {
      console.error('Failed to fetch divisions', error);
    }
  };

  // Main data fetch with filters using hook
  const {
    users,
    isLoading,
    isFetching,
    filterCounts,
    totalItems: totalUsers,
    totalPages,
    response: usersResponse,
    refreshData
  } = useUserPageData({
    queryKey: [
      'users',
      debouncedSearchQuery,
      statusFilter,
      selectedRoleIds,
      currentPage,
      pageSize,
      debouncedColumnFilters,
      sortConfig
    ],
    pageSize, // Pass pageSize for correct totalPages calculation
    queryFn: () => {
      const filters: UserFilters = {
        search: debouncedSearchQuery || undefined,
        role_ids: selectedRoleIds.length > 0 ? selectedRoleIds : undefined,
        columnFilters: getColumnFiltersParam() || undefined,
        sortBy: sortConfig.field,
        order: sortConfig.direction
      };

      // Map statusFilter to actual filter parameters
      if (statusFilter === 'active') filters.is_active = 'true';
      if (statusFilter === 'inactive') filters.is_active = 'false';
      if (statusFilter === 'contacts') filters.contacts = 'true';

      return userAdminService.getAllUsers(filters, currentPage, pageSize);
    }
  });

  const closeCreateModal = () => {
    setIsCreateModalOpen(false);
    setCreateFormRoleIds([]);
    setCreateFormContactStatus('');
    setCreateFormOverrides(undefined);
    setCreateExternalErrors({});
  };

  const closeEditModal = () => {
    setIsEditModalOpen(false);
    setEditFormRoleIds([]);
    setEditFormContactStatus(CONTACT_STATUS.ACTIVE);
    setEditFormOverrides(undefined);
    setEditExternalErrors({});
  };

  // Shared by create and edit: server field errors under their fields, the rest as a toast.
  const showSubmitError = (
    error: unknown,
    fields: FormField[],
    setExternalErrors: (errors: Record<string, string>) => void,
    fallbackMessage: string,
    conflictErrors?: Record<string, string>
  ) => {
    const { fieldErrors, toastMessage } = resolveUserFormSubmitError(error, fields, fallbackMessage, conflictErrors);
    setExternalErrors(fieldErrors);
    if (toastMessage) showToast.error(toastMessage);
  };

  const handleCreate = async (data: UserFormData) => {
    setIsSubmitting(true);
    setCreateExternalErrors({});
    try {
      if (toRoleIds(data.role_ids).length > 0 && isDefaultUserId(data.user_id)) {
        setCreateExternalErrors({ user_id: USER_ID_REQUIRED_FOR_ROLES });
        return;
      }
      const roleIds = toRoleIdStrings(data.role_ids);
      const authProvider = isMsalProvider ? 'microsoft' : 'keycloak';
      const payload = buildCreatePayload(data, {
        // Division is shown (and sent) only for roles that need it; hidden → key omitted (D-014)
        divisionVisible: isDivisionRequired(roleIds),
        authSource: roleIds.length > 0 ? authProvider : 'none',
        canSetPreferredName: canEditPreferredName
      });
      await userAdminService.createUser(payload);
      closeCreateModal();
      // Refresh users data
      refreshData();
    } catch (error) {
      console.error('Create failed', error);
      showSubmitError(error, createUserFormFields, setCreateExternalErrors, 'Failed to create user', {
        email: DUPLICATE_EMAIL_MESSAGE
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // The names of blocked roles the edit would remove (they have active assignments).
  const getBlockedRoleRemovals = (user: AdminUser, data: UserFormData): string[] => {
    const blockedRoles = user.roleDependencies?.blockedRoles ?? [];
    if (blockedRoles.length === 0 || !data.role_ids) return [];
    const newRoleIds = new Set(toRoleIds(data.role_ids));
    return user.role_ids
      .filter((id) => !newRoleIds.has(id))
      .map((roleId) => user.role_names[user.role_ids.indexOf(roleId)])
      .filter((role) => Boolean(role) && blockedRoles.includes(role));
  };

  const handleUpdate = async (data: UserFormData) => {
    if (!selectedUser) return;

    const blockedRemovals = getBlockedRoleRemovals(selectedUser, data);
    if (blockedRemovals.length > 0) {
      const details = Object.entries(selectedUser.roleDependencies?.counts || {})
        .map(([key, count]) => `${key.replace(/_/g, ' ')}: ${count}`)
        .join(', ');
      showToast.error(`Cannot remove role(s): ${blockedRemovals.join(', ')}. Active assignments: ${details}`);
      return;
    }

    setIsSubmitting(true);
    setEditExternalErrors({});
    try {
      if (toRoleIds(data.role_ids).length > 0 && isDefaultUserId(data.user_id)) {
        setEditExternalErrors({ user_id: USER_ID_REQUIRED_FOR_ROLES });
        return;
      }
      // Get only the fields that have changed. While the Division field is hidden for
      // the new roles, division_id is not diffed or sent, so the stored one is kept (D-014).
      const changes = getChangedFields(toEditFormOriginal(selectedUser), data, {
        includeDivision: isDivisionRequired(toRoleIdStrings(data.role_ids))
      });

      // Only proceed if there are actual changes
      if (Object.keys(changes).length === 0) {
        closeEditModal();
        return;
      }

      await userAdminService.updateUser(selectedUser.id, changes);
      closeEditModal();
      // Refresh users data
      refreshData();
    } catch (error) {
      console.error('Update failed', error);
      showSubmitError(error, editFormFields, setEditExternalErrors, 'Failed to update user');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedUser) return;
    setIsSubmitting(true);
    try {
      await userAdminService.deleteUser(selectedUser.id);
      setIsDeleteModalOpen(false);
      // Refresh users data
      refreshData();
    } catch (error) {
      console.error('Delete failed', error);
      showToast.error(getApiErrorMessage(error, 'Failed to delete user'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeactivate = async () => {
    if (!selectedUser) return;
    setIsSubmitting(true);
    try {
      await userAdminService.updateUser(selectedUser.id, { is_active: false });
      setIsDeactivateModalOpen(false);
      closeEditModal();
      refreshData();
      showToast.success('User deactivated successfully');
    } catch (error) {
      console.error('Deactivate failed', error);
      showToast.error(getApiErrorMessage(error, 'Failed to deactivate user'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSendTemporaryPassword = async () => {
    if (!selectedUser) return;
    setIsSendingTemporaryPassword(true);
    try {
      await userAdminService.sendTemporaryPassword(selectedUser.id);
      setIsSendPasswordModalOpen(false);
      showToast.success('Temporary password sent');
    } catch (error) {
      console.error('Send temporary password failed', error);
      showToast.error(getApiErrorMessage(error, 'Failed to send temporary password'));
    } finally {
      setIsSendingTemporaryPassword(false);
    }
  };

  const handleSearch = (value: string) => {
    setSearchQuery(value);
    resetToFirstPage();
  };

  const handleFilterChange = (filter: string) => {
    setStatusFilter(filter);
    resetToFirstPage();
  };

  const handleRoleFilterChange = (roleIds: number[]) => {
    setSelectedRoleIds(roleIds);
    resetToFirstPage();
  };

  const toggleColumn = (columnKey: string) => {
    const column = userColumns.find((col) => col.key === columnKey);
    if (column?.alwaysVisible) return;

    setVisibleColumns((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(columnKey)) {
        newSet.delete(columnKey);
      } else {
        newSet.add(columnKey);
      }
      return newSet;
    });
  };

  // Export mutation - fetches the full filtered dataset (all pages), not just the current page
  const exportMutation = useMutation({
    mutationFn: () => {
      const filters: UserFilters = {
        search: debouncedSearchQuery || undefined,
        role_ids: selectedRoleIds.length > 0 ? selectedRoleIds : undefined,
        columnFilters: getColumnFiltersParam() || undefined,
        sortBy: sortConfig.field,
        order: sortConfig.direction
      };

      if (statusFilter === 'active') filters.is_active = 'true';
      if (statusFilter === 'inactive') filters.is_active = 'false';
      if (statusFilter === 'contacts') filters.contacts = 'true';

      return userAdminService.getAllUsersForExport(filters);
    },
    onSuccess: (response) => {
      exportUsers(response.data, Array.from(visibleColumns));
    },
    onError: (err: unknown) => {
      console.error('Failed to export users:', err);
      showToast.error(getApiErrorMessage(err, 'Failed to export users. Please try again.'));
    }
  });

  const handleExport = () => {
    exportMutation.mutate();
  };

  // Cell renderers - extracted for better maintainability
  const renderRoleCell = useCallback((user: AdminUser) => {
    if (!user.role_names || user.role_names.length === 0) {
      return <Badge variant="default">Contact</Badge>;
    }

    const maxVisibleRoles = 2;
    const visibleRoles = user.role_names.slice(0, maxVisibleRoles);
    const remainingCount = user.role_names.length - maxVisibleRoles;
    const allRolesText = user.role_names.join(', ');

    return (
      <span className={styles.roleBadges} title={user.role_names.length > maxVisibleRoles ? allRolesText : undefined}>
        {visibleRoles.map((role) => (
          <Badge key={`role-${role}`} variant="info">
            {role}
          </Badge>
        ))}
        {remainingCount > 0 && (
          <Badge variant="default" className={styles.roleBadgeMore}>
            +{remainingCount}
          </Badge>
        )}
      </span>
    );
  }, []);

  const renderAuthSourceCell = useCallback((user: AdminUser) => {
    if (user.auth_source === 'microsoft') {
      return (
        <div className={styles.cellCenter}>
          <img src="/microsoft-logo.svg" alt="Microsoft" title="Microsoft SSO" className={styles.authIcon} />
        </div>
      );
    }

    if (user.auth_source === 'keycloak') {
      return (
        <div className={styles.cellCenter}>
          <div title="Keycloak SSO"><KeycloakIcon className={styles.authIcon} /></div>
        </div>
      );
    }

    if (user.auth_source === 'none') {
      return (
        <div className={styles.cellCenter}>
          <div title="No Authentication">
            <UserX size={16} color="#9ca3af" />
          </div>
        </div>
      );
    }

    return (
      <div className={styles.cellCenter}>
        <span title="Local Password">🔑</span>
      </div>
    );
  }, []);

  const renderAccountSourceCell = useCallback((user: AdminUser) => {
    const sourceLabels: Record<string, string> = {
      manual: 'Manually Created',
      microsoft_sync: 'Imported from Microsoft',
      keycloak_sync: 'Imported from Keycloak',
      data_import: 'Imported via Data'
    };

    return (
      <div className={styles.cellCenter}>
        <Badge variant={user.account_source === 'manual' ? 'info' : 'default'}>
          {sourceLabels[user.account_source] || user.account_source}
        </Badge>
      </div>
    );
  }, []);

  const renderActionsCell = useCallback((user: AdminUser) => {
    const openUserDetails = async () => {
      try {
        const fullUser = await userAdminService.getUser(user.id);
        setSelectedUser(fullUser);
        setEditFormRoleIds(fullUser.role_ids?.map((id: number) => id.toString()) || []);
        setEditFormContactStatus(initialContactStatus(fullUser));
        setEditFormOverrides(undefined);
        setEditExternalErrors({});
        setIsEditModalOpen(true);
      } catch (error) {
        console.error('Failed to fetch user details', error);
        showToast.error(getApiErrorMessage(error, 'Failed to load user details'));
      }
    };

    if (!canManageContactUsers) {
      return (
        <div className={styles.actionButtons}>
          <Button
            size="sm"
            variant="ghost"
            onClick={openUserDetails}
            title="View User"
            className="hover:bg-blue-50"
            data-test-id="action-button-view"
          >
            <Eye size={16} color="#2563eb" />
          </Button>
        </div>
      );
    }

    return (
      <div className={styles.actionButtons}>
        <Button
          size="sm"
          variant="ghost"
          onClick={openUserDetails}
          title="Edit User"
          className="hover:bg-blue-50"
          data-test-id="action-button-edit"
        >
          <Edit size={16} color="#2563eb" />
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => {
            setSelectedUser(user);
            setIsDeleteModalOpen(true);
          }}
          title="Delete User"
          className="hover:bg-red-50"
          data-test-id="action-button-delete"
        >
          <Trash2 size={16} color="#dc2626" />
        </Button>
      </div>
    );
  }, [canManageContactUsers]);

  const renderCell = useCallback(
    (column: TableColumn, user: AdminUser) => {
      switch (column.key) {
        case 'email':
          return user.email;
        case 'first_name':
          return `${formatPersonName(user)}${user.is_active ? '' : ' (Inactive)'}`;
        case 'contact_id':
          return user.contact_id ?? '';
        case 'user_id':
          return user.user_id ?? '';
        case 'role_names':
          return renderRoleCell(user);
        case 'division_name':
          return user.division_name || '';
        case 'auth_source':
          return renderAuthSourceCell(user);
        case 'account_source':
          return renderAccountSourceCell(user);
        case 'is_active':
          return (
            <div className={styles.cellCenter}>
              <Badge variant={user.is_active ? 'success' : 'error'}>{user.is_active ? 'Active' : 'Inactive'}</Badge>
            </div>
          );
        case 'can_login':
          return (
            <div className={styles.cellCenter}>
              <Badge variant={user.can_login ? 'success' : 'default'}>{user.can_login ? 'Enabled' : 'Disabled'}</Badge>
            </div>
          );
        case 'actions':
          return renderActionsCell(user);
        default:
          return null;
      }
    },
    [renderRoleCell, renderAuthSourceCell, renderAccountSourceCell, renderActionsCell]
  );

  // Helper to check if role requires division field
  const contractRole = roles.find((r) => r.name === ROLES.CONTRACT_ROLE);
  const INTERNAL_ROLE_NAMES = new Set<string>([
    ROLES.ADMIN,
    ROLES.PROGRAM_LEAD,
    ROLES.SCHEDULER,
    ROLES.ASSESSOR,
    ROLES.EXECUTIVE
  ]);

  const warnIfInternalPlusContractRole = (roleIds: string[]) => {
    const selectedNames = roleIds
      .map((id) => roles.find((role) => role.id.toString() === id)?.name)
      .filter((name): name is string => Boolean(name));
    const hasContractRole = selectedNames.includes(ROLES.CONTRACT_ROLE);
    const hasInternalRole = selectedNames.some((name) => INTERNAL_ROLE_NAMES.has(name));
    if (hasContractRole && hasInternalRole) {
      showToast.warning(
        'Internal staff can only be given the Contract Role if they are already assigned as Contract Owner or Contract Manager on a contract.'
      );
    }
  };

  // Division is required for: Contract Role OR No Role (Contact)
  const isDivisionRequired = (roleIds: string[]) => {
    if (!roleIds || roleIds.length === 0) return true;
    if (contractRole && roleIds.includes(contractRole.id.toString())) return true;
    return false;
  };

  // Preselected on the create form; '' (no preselection) if the division has been renamed or deactivated.
  const unidentifiedDivisionId =
    divisions.find((div) => div.name.toLowerCase() === UNIDENTIFIED_DIVISION_NAME.toLowerCase())?.id.toString() ?? '';

  const isEditingSelf = selectedUser?.id !== undefined && selectedUser.id === currentUser?.id;
  // PreferredName is stored for an Admin only (D-010); for anyone else the field is disabled and not sent.
  const canEditPreferredName = hasRole(currentUser, ROLES.ADMIN);

  const createUserFormFields = buildCreateUserFormFields({
    roles,
    divisions,
    canEditPreferredName,
    divisionVisible: isDivisionRequired(createFormRoleIds),
    canLogin: getCanLoginFieldState(createFormRoleIds, createFormContactStatus),
    userId: getUserIdFieldState(createFormRoleIds)
  });

  const editableUserFormFields = buildEditUserFormFields({
    roles,
    divisions,
    canEditPreferredName,
    divisionVisible: isDivisionRequired(editFormRoleIds),
    canLogin: getCanLoginFieldState(editFormRoleIds, editFormContactStatus),
    userId: getUserIdFieldState(editFormRoleIds),
    selectedUser,
    isEditingSelf,
    isMsalProvider
  });
  const editFormFields = canManageContactUsers
    ? editableUserFormFields
    : editableUserFormFields.map((field) => ({ ...field, disabled: true }));

  const handleCreateFieldChange = (name: string, value: UserFormValue) => {
    if (name === 'role_ids') {
      const userIdOverride = getRoleChangeUserIdOverrides(name, value, createFormRoleIds);
      setCreateFormRoleIds(toRoleIdStrings(value));
      if (userIdOverride) setCreateFormOverrides(userIdOverride);
    }
    if (name === 'contact_status') setCreateFormContactStatus(toContactStatusFormValue(value));
    const overrides = getContactStatusOverrides(name, value);
    if (overrides) {
      setCreateFormOverrides(overrides);
      if (overrides.role_ids !== undefined) setCreateFormRoleIds(toRoleIdStrings(overrides.role_ids));
    }
    // Changing a field clears that field's server error; a role change also rewrites UserID.
    setCreateExternalErrors((prev) => {
      const next = withoutKey(prev, name);
      return name === 'role_ids' ? withoutKey(next, 'user_id') : next;
    });
  };

  const handleEditFieldChange = (name: string, value: UserFormValue) => {
    if (name === 'role_ids') {
      const userIdOverride = getRoleChangeUserIdOverrides(name, value, editFormRoleIds);
      setEditFormRoleIds(toRoleIdStrings(value));
      if (userIdOverride) setEditFormOverrides(userIdOverride);
    }
    if (name === 'contact_status') setEditFormContactStatus(toContactStatusFormValue(value) || CONTACT_STATUS.ACTIVE);
    const overrides = getContactStatusOverrides(name, value);
    if (overrides) {
      setEditFormOverrides(overrides);
      if (overrides.role_ids !== undefined) setEditFormRoleIds(toRoleIdStrings(overrides.role_ids));
    }
    setEditExternalErrors((prev) => {
      const next = withoutKey(prev, name);
      return name === 'role_ids' ? withoutKey(next, 'user_id') : next;
    });
  };

  return (
    <PageLayout title="User Management" showHeader={false}>
      <ListPageHeader
        icon={<Users size={24} />}
        title="User Management"
        count={filterCounts.all || 0}
        searchValue={searchQuery}
        onSearchChange={handleSearch}
        searchPlaceholder="Search users by name, email..."
        actions={
          <>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleExport}
              disabled={exportMutation.isPending || users.length === 0}
            >
              <Download size={16} />
              {exportMutation.isPending ? 'Exporting...' : 'Export List'}
            </Button>

            <ColumnSelector
              columns={userColumns}
              visibleColumns={Array.from(visibleColumns)}
              onToggleColumn={toggleColumn}
            />
          </>
        }
        additionalContent={
          canManageContactUsers && (
            <>
              <Button variant="primary" size="sm" onClick={() => setIsCreateModalOpen(true)}>
                <Plus size={16} />
                Add User
              </Button>

              <Button variant="secondary" size="sm" onClick={() => setIsSyncModalOpen(true)}>
                {isMsalProvider ? 'Sync from Microsoft' : 'Sync from Keycloak'}
              </Button>
            </>
          )
        }
      />

      <div className={styles.filters}>
        <FilterButtons
          filters={statusFilters}
          activeFilter={statusFilter}
          onFilterChange={handleFilterChange}
          filterCounts={filterCounts}
          dataTestId="users-filter"
        />

        <RoleFilterDropdown
          roles={roles}
          selectedRoleIds={selectedRoleIds}
          onSelectionChange={handleRoleFilterChange}
          roleCounts={usersResponse?.statusTotals?.roles || []}
          dataTestId="role-filter"
        />
      </div>

      {/* Loading overlay when fetching data */}
      {isFetching && !isLoading && <LoadingOverlay variant="page" />}

      <RZTable
        data={users}
        columns={userColumns.filter((col) => visibleColumns.has(col.key))}
        getRowKey={(user) => user.id}
        renderCell={renderCell}
        rowClassName={(user) => (!user.role_ids || user.role_ids.length === 0 ? 'user-management__contact-row' : '')}
        sorting={{
          sortConfig,
          onSort: handleSort,
          isSorted
        }}
        columnFilters={{
          values: columnFilterValues,
          onChange: setColumnFilter,
          onClearAll: clearColumnFilters,
          hasActiveFilters: hasActiveColumnFilters
        }}
        pagination={{
          currentPage,
          totalPages,
          totalItems: totalUsers,
          pageSize,
          onPageChange: setCurrentPage,
          onPageSizeChange: setPageSize,
          pageSizeOptions: [25, 50, 100],
          itemName: 'users'
        }}
        state={{
          isLoading,
          isEmpty: users.length === 0,
          emptyTitle: 'No users found',
          emptyDescription: 'Try adjusting your search or filters.',
          emptyType: 'default',
          loadingMessage: 'Loading users...'
        }}
      />

      <GenericModal
        isOpen={isCreateModalOpen}
        onClose={closeCreateModal}
        title="Create New User"
        size="lg"
        disableBackdropClick
        dataTestId="add-user-modal"
      >
        <div className={styles.modalContent}>
          <GenericForm
            // Divisions load asynchronously and initialData is read once: remount when the
            // Unidentified default becomes known so a form opened early still gets it.
            key={unidentifiedDivisionId}
            fields={createUserFormFields}
            initialData={{
              user_id: NON_USER_ID,
              auth_source: 'None',
              can_login: 'false',
              role_ids: [],
              division_id: unidentifiedDivisionId
            }}
            onSubmit={handleCreate}
            onCancel={closeCreateModal}
            onFieldChange={handleCreateFieldChange}
            externalValues={createFormOverrides}
            externalErrors={createExternalErrors}
            isSubmitting={isSubmitting}
          />
        </div>
      </GenericModal>

      <GenericModal
        isOpen={isEditModalOpen}
        onClose={closeEditModal}
        title={canManageContactUsers ? 'Edit User' : 'User Details'}
        size="lg"
        dataTestId="edit-user-modal"
      >
        {selectedUser?.roleDependencies?.hasDependencies && (
          <div className={styles.dependencyWarning} data-test-id="dependency-warning">
            <div className={styles.dependencyWarningTitle}>⚠️ Role Dependencies Detected</div>
            <p className={styles.dependencyWarningText}>
              The following roles cannot be removed due to active assignments:
            </p>
            <ul className={styles.dependencyWarningList}>
              {selectedUser.roleDependencies.blockedRoles.map((role: string) => (
                <li key={role}>
                  <strong>{role}</strong>
                </li>
              ))}
            </ul>
            <p className="user-management__dependency-warning-text" style={{ fontSize: '12px', marginTop: '8px' }}>
              Details:{' '}
              {Object.entries(selectedUser.roleDependencies.counts)
                .map(([key, count]) => `${key.replace(/_/g, ' ')}: ${count}`)
                .join(', ')}
            </p>
            <p className={styles.dependencyWarningText}>
              You can still add new roles or remove other roles without dependencies.
            </p>
          </div>
        )}
        <div className={styles.modalContent}>
        <GenericForm
          fields={editFormFields}
          initialData={toEditFormInitialData(selectedUser)}
          onSubmit={handleUpdate}
          onCancel={closeEditModal}
          onFieldChange={handleEditFieldChange}
          externalValues={editFormOverrides}
          externalErrors={editExternalErrors}
          isSubmitting={isSubmitting}
          hideActions={!canManageContactUsers}
        >
          {canManageContactUsers && selectedUser?.is_active && (
            <div className={styles.deactivateAction}>
              <Button
                type="button"
                variant="danger"
                dataTestId="deactivate-user-button"
                disabled={isSubmitting || isEditingSelf || Boolean(selectedUser.dependencies?.hasDependencies)}
                title={
                  isEditingSelf
                    ? 'You cannot deactivate your own account'
                    : selectedUser.dependencies?.hasDependencies
                      ? 'Cannot deactivate: User has active dependencies'
                      : 'Deactivate User'
                }
                onClick={() => setIsDeactivateModalOpen(true)}
              >
                Deactivate User
              </Button>
            </div>
          )}
          {canManageContactUsers && selectedUser && isEligibleForTemporaryPassword(selectedUser) && (
            <div className={styles.formExtraActions}>
              <Button
                type="button"
                variant="secondary"
                dataTestId="send-temporary-password-button"
                disabled={isSubmitting || isSendingTemporaryPassword}
                onClick={() => setIsSendPasswordModalOpen(true)}
              >
                Send Temporary Password
              </Button>
            </div>
          )}
        </GenericForm>
        </div>
      </GenericModal>

      <ConfirmActionModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDelete}
        title="Delete User"
        message={`Are you sure you want to delete ${selectedUser?.email}? This action cannot be undone.`}
        isSubmitting={isSubmitting}
        actionType="delete"
        dependencies={selectedUser?.dependencies?.counts}
      />

      <ConfirmActionModal
        isOpen={isDeactivateModalOpen}
        onClose={() => setIsDeactivateModalOpen(false)}
        onConfirm={handleDeactivate}
        title="Deactivate User"
        message={`Are you sure you want to deactivate ${selectedUser ? formatPersonName(selectedUser) : 'this user'} (${selectedUser?.email})? They will no longer be able to sign in.`}
        isSubmitting={isSubmitting}
        actionType="deactivate"
      />

      <ConfirmActionModal
        isOpen={isSendPasswordModalOpen}
        onClose={() => setIsSendPasswordModalOpen(false)}
        onConfirm={handleSendTemporaryPassword}
        title="Send Temporary Password"
        message={`This will generate a new temporary password and email it to ${selectedUser?.email}. The previous password will stop working.`}
        isSubmitting={isSendingTemporaryPassword}
        actionType="warning"
      />

      {isMsalProvider ? (
        <UserSyncModal
          isOpen={isSyncModalOpen}
          onClose={() => setIsSyncModalOpen(false)}
          title="Sync from Microsoft Azure AD"
          syncUrl="/admin/users/microsoft/sync"
          getName={(u) => (u as unknown as { displayName: string }).displayName}
          getEmail={(u) => {
            const user = u as unknown as { mail?: string; userPrincipalName?: string };
            return user.mail || user.userPrincipalName || '';
          }}
          onImport={async (users) => {
            await userAdminService.importMicrosoftUsers(users as unknown as Parameters<typeof userAdminService.importMicrosoftUsers>[0]);
            refreshData();
          }}
          dataTestId="microsoft-sync-modal"
        />
      ) : (
        <UserSyncModal
          isOpen={isSyncModalOpen}
          onClose={() => setIsSyncModalOpen(false)}
          title="Sync from Keycloak"
          syncUrl="/admin/users/keycloak/sync"
          getName={(u) => {
            const user = u as unknown as { firstName?: string; lastName?: string; username: string };
            return [user.firstName, user.lastName].filter(Boolean).join(' ') || user.username;
          }}
          getEmail={(u) => {
            const user = u as unknown as { email?: string; username: string };
            return user.email || user.username;
          }}
          onImport={async (users) => {
            await userAdminService.importKeycloakUsers(users as unknown as Parameters<typeof userAdminService.importKeycloakUsers>[0]);
            refreshData();
          }}
          dataTestId="keycloak-sync-modal"
        />
      )}
    </PageLayout>
  );
};

export default UserManagementPage;
